import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, AppBar, Toolbar, Typography, IconButton, Button, Card, CardContent,
  Chip, LinearProgress, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, Switch,
  Tooltip, Divider, TextField,
} from '@mui/material';
import {
  ArrowBack, Map as MapIcon, CloudUpload, Edit, Add, Delete,
  CheckCircle, Warning, Error as ErrorIcon, Refresh, Print,
} from '@mui/icons-material';
import { useAuth } from './AuthContext';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface Area { id: number; nome: string; zona: string; total_quarteiroes: number; total_imoveis: number; }
interface Quarteirao {
  id: number; codigo: string; total_imoveis: number;
  total_r: number; total_c: number; total_tb: number; total_pe: number; total_o: number;
  status: string; criado_em: string; atualizado_em: string;
  motivo?: string | null;   // 20/10/2026: só nos quarteirões FORA_DE_USO
}
interface Imovel {
  id: number; logradouro: string | null; lado: string | null; numero: string | null;
  sequencia: string | null; complemento: string | null; tipo: string;
  folha_origem: number | null; ordem_origem: number | null;
  necessita_revisao: boolean; observacao: string | null;
}
type Situacao = 'trabalhado' | 'fechado' | 'recusado' | 'recuperado' | 'nao_trabalhado';
interface ImovelSituacao extends Omit<Imovel, 'folha_origem' | 'ordem_origem'> {
  situacao: Situacao;
  conferido: boolean;
  fad01_id: number | null;
  data_situacao: string | null;
  agente_id: number | null;
  agente_nome: string | null;
}
interface PainelQrt extends Quarteirao {
  status_painel: 'done' | 'worked' | 'pending';
  concluido: boolean; concluido_em: string | null; concluido_por: string | null;
  trabalhado_neste_ciclo: boolean;
  sit_trabalhados: number; sit_fechados: number; sit_recusados: number;
  sit_recuperados: number; sit_conferidos: number;
}
interface PainelData {
  metricas: {
    total_quarteiroes: number; concluidos: number; trabalhados: number; pendentes: number;
    nao_cadastrados: number; pct_quarteiroes: number;
    total_imoveis: number; imoveis_cobertos: number; pct_imoveis: number;
  };
  quarteiroes: PainelQrt[];
  inconsistencias: { tipo: string; codigo: string; texto: string }[];
  fora_de_uso?: { id: number; codigo: string; motivo: string | null }[];
}
interface Ciclo { id: number; nome: string; ativo?: boolean; }
interface PreviewQrt {
  quarteirao: string; arquivo_origem: string; status: string; obs_status: string | null;
  total: number; total_r: number; total_c: number; total_tb: number; total_pe: number; total_o: number;
  duplicata: boolean; quarteirao_id_existente: number | null;
}

// ─── Estilos compartilhados ────────────────────────────────────────────────────

const TH: React.CSSProperties = {
  background: '#0a1830', color: '#DCE6FB', padding: '9px 10px',
  fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.5px', textAlign: 'center', whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'center', fontSize: '0.82rem',
  borderBottom: '1px solid #E5E7EB', fontVariantNumeric: 'tabular-nums',
};
const SecaoHeader = ({ titulo, cor = '#1B3A6B', right }: { titulo: string; cor?: string; right?: React.ReactNode }) => (
  <Box sx={{ background: cor, color: '#fff', px: 2.5, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '14px 14px 0 0' }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{titulo}</Typography>
    {right}
  </Box>
);
const SCard = ({ titulo, cor, right, children }: { titulo: string; cor?: string; right?: React.ReactNode; children: React.ReactNode }) => (
  <Card sx={{ borderRadius: 3, border: '1px solid #BFDBFE', overflow: 'hidden' }}>
    <SecaoHeader titulo={titulo} cor={cor} right={right} />
    <CardContent sx={{ p: 2.5 }}>{children}</CardContent>
  </Card>
);
const StatusChip = ({ s }: { s: string }) => {
  const map: Record<string, { label: string; bg: string; color: string }> = {
    VALIDADO:        { label: 'Validado',       bg: '#D1FAE5', color: '#065F46' },
    DIVERGENTE:      { label: 'Divergente',     bg: '#FEE2E2', color: '#991B1B' },
    PENDENTE_REVISAO:{ label: 'Pend. revisão',  bg: '#FEF3C7', color: '#92400E' },
    ERRO_LEITURA:    { label: 'Erro leitura',   bg: '#FEE2E2', color: '#991B1B' },
  };
  const m = map[s] || { label: s, bg: '#F3F4F6', color: '#374151' };
  return <Chip label={m.label} size="small" sx={{ bgcolor: m.bg, color: m.color, fontWeight: 700, fontSize: '0.7rem' }} />;
};
const API = '/api/rg';

// ─── TELA 1: Lista de Áreas ────────────────────────────────────────────────────

function TelaListaAreas({ onSelect }: { onSelect: (area: Area, tela: 'importar' | 'cadastro' | 'painel') => void }) {
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/areas`).then(r => r.json()).then(setAreas).finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SCard titulo="Áreas Cadastradas" right={
        <Typography sx={{ fontSize: '0.72rem', color: '#BFDBFE' }}>{areas.length} área(s)</Typography>
      }>
        {areas.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#6B7280' }}>
            <MapIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
            <Typography sx={{ fontWeight: 600 }}>Nenhuma área com RG cadastrado</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2.5 }}>
            {areas.map(a => (
              <Card key={a.id} sx={{ border: '1px solid #BFDBFE', borderRadius: 3, p: 2.5, cursor: 'pointer', transition: 'all .15s', '&:hover': { boxShadow: '0 4px 16px rgba(15,35,71,.12)', transform: 'translateY(-2px)' } }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0F2347', mb: 0.5 }}>{a.nome}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, mb: 2 }}>{a.zona}</Typography>
                <Box sx={{ display: 'flex', gap: 3, mb: 2.5 }}>
                  <Box><Typography sx={{ fontWeight: 800, fontSize: '1.4rem', fontVariantNumeric: 'tabular-nums', color: '#0F2347' }}>{a.total_quarteiroes}</Typography><Typography sx={{ fontSize: '0.65rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>Quarteirões</Typography></Box>
                  <Box><Typography sx={{ fontWeight: 800, fontSize: '1.4rem', fontVariantNumeric: 'tabular-nums', color: '#0F2347' }}>{a.total_imoveis}</Typography><Typography sx={{ fontSize: '0.65rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>Imóveis</Typography></Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="contained" startIcon={<CloudUpload sx={{ fontSize: 14 }} />}
                    sx={{ bgcolor: '#065F46', '&:hover': { bgcolor: '#047857' }, fontSize: '0.72rem', fontWeight: 700, borderRadius: 1.5 }}
                    onClick={() => onSelect(a, 'importar')}>Importar RG</Button>
                  {a.total_quarteiroes > 0 && <>
                    <Button size="small" variant="outlined" startIcon={<Edit sx={{ fontSize: 14 }} />}
                      sx={{ fontSize: '0.72rem', fontWeight: 700, borderRadius: 1.5 }}
                      onClick={() => onSelect(a, 'cadastro')}>Cadastro</Button>
                    <Button size="small" variant="outlined" startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                      sx={{ fontSize: '0.72rem', fontWeight: 700, borderRadius: 1.5 }}
                      onClick={() => onSelect(a, 'painel')}>Painel</Button>
                  </>}
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </SCard>
    </Box>
  );
}

// ─── TELA 2: Importação ────────────────────────────────────────────────────────

function TelaImportacao({ area, onBack }: { area: Area; onBack: () => void }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [arquivos, setArquivos] = useState<FileList | null>(null);
  const [preview, setPreview] = useState<{ resumo: any; quarteiroes: PreviewQrt[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [revisado, setRevisado] = useState(false);
  const [acaoDup, setAcaoDup] = useState<'substituir' | 'cancelar'>('substituir');
  const [resultado, setResultado] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const temDuplicata = preview?.quarteiroes.some(q => q.duplicata) ?? false;
  const podeImportar = revisado && step === 2;

  async function fazerPreview(files: FileList) {
    setLoading(true); setErro(null);
    const fd = new FormData();
    fd.append('area_id', String(area.id));
    Array.from(files).forEach(f => fd.append('arquivos', f));
    try {
      const r = await fetch(`${API}/importar/preview`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erro no preview');
      setPreview(d); setStep(2);
    } catch (e: any) { setErro(e.message); }
    finally { setLoading(false); }
  }

  async function confirmarImportacao() {
    if (!arquivos) return;
    setLoading(true); setErro(null);
    const fd = new FormData();
    fd.append('area_id', String(area.id));
    fd.append('acao_duplicata', acaoDup);
    fd.append('importado_por', 'supervisor');
    Array.from(arquivos).forEach(f => fd.append('arquivos', f));
    try {
      const r = await fetch(`${API}/importar/confirmar`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erro na importação');
      setResultado(d); setStep(3);
    } catch (e: any) { setErro(e.message); }
    finally { setLoading(false); }
  }

  const Steps = () => (
    <Box sx={{ display: 'flex', gap: 3, mb: 0.5 }}>
      {[['1', 'Upload'], ['2', 'Revisão'], ['3', 'Importado']].map(([n, label], i) => {
        const done = step > i + 1; const active = step === i + 1;
        return (
          <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, bgcolor: done ? '#065F46' : active ? '#1B3A6B' : '#E5E7EB', color: done || active ? '#fff' : '#9CA3AF' }}>
              {done ? <CheckCircle sx={{ fontSize: 15 }} /> : n}
            </Box>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: active ? '#0F2347' : done ? '#065F46' : '#9CA3AF' }}>{label}</Typography>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <SCard titulo={`Importação de Boletins RG — ${area.nome}`} right={<Steps />}>
        <Box sx={{ height: 4 }} />
      </SCard>

      {erro && <Alert severity="error" onClose={() => setErro(null)}>{erro}</Alert>}

      {step === 1 && (
        <SCard titulo="Upload dos arquivos .xlsx">
          <Box
            onClick={() => inputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files; if (f.length) { setArquivos(f); fazerPreview(f); } }}
            sx={{ border: '2px dashed #BFDBFE', borderRadius: 3, p: 6, textAlign: 'center', color: '#6B7280', bgcolor: '#F8FAFF', cursor: 'pointer', transition: 'all .15s', '&:hover': { borderColor: '#065F46', bgcolor: '#EFF6FF' } }}
          >
            {loading ? <CircularProgress size={36} /> : <>
              <CloudUpload sx={{ fontSize: 48, color: '#1B3A6B', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, color: '#0F2347', mb: 0.5 }}>Arraste os arquivos .xlsx ou clique para selecionar</Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>Boletins RG — aba "Base TOVIS" obrigatória · múltiplos arquivos aceitos</Typography>
            </>}
          </Box>
          <input ref={inputRef} type="file" multiple accept=".xlsx" style={{ display: 'none' }}
            onChange={e => { if (e.target.files?.length) { setArquivos(e.target.files); fazerPreview(e.target.files); } }} />
        </SCard>
      )}

      {step === 2 && preview && (
        <>
          <SCard titulo="Resumo da importação">
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                { label: 'Quarteirões', v: preview.resumo.total_quarteiroes, color: '#0F2347' },
                { label: 'Imóveis', v: preview.resumo.total_imoveis, color: '#0F2347' },
                { label: 'Validados', v: preview.resumo.validados, color: '#065F46' },
                { label: 'Divergentes', v: preview.resumo.divergentes, color: '#991B1B' },
                { label: 'Pend. revisão', v: preview.resumo.pendentes_revisao, color: '#92400E' },
              ].map(({ label, v, color }) => (
                <Box key={label}>
                  <Typography sx={{ fontWeight: 800, fontSize: '2rem', fontVariantNumeric: 'tabular-nums', color }}>{v}</Typography>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </SCard>

          {temDuplicata && (
            <Alert severity="warning" icon={<Warning />}>
              <strong>Duplicidade detectada:</strong> alguns quarteirões já existem no cadastro desta área. O que deseja fazer?
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                <Button size="small" variant={acaoDup === 'substituir' ? 'contained' : 'outlined'} sx={{ fontWeight: 700 }} onClick={() => setAcaoDup('substituir')}>Substituir existente</Button>
                <Button size="small" variant={acaoDup === 'cancelar' ? 'contained' : 'outlined'} color="warning" sx={{ fontWeight: 700 }} onClick={() => setAcaoDup('cancelar')}>Pular duplicados</Button>
              </Box>
            </Alert>
          )}

          <SCard titulo="Preview dos quarteirões">
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  {['Código', 'Arquivo', 'Total', 'R', 'C', 'TB', 'PE', 'O', 'Status', 'Obs'].map(h => <th key={h} style={{ ...TH, textAlign: h === 'Código' || h === 'Arquivo' || h === 'Obs' ? 'left' : 'center' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {preview.quarteiroes.map((q, i) => (
                    <tr key={i} style={{ background: q.duplicata ? '#FFF7ED' : i % 2 === 0 ? '#fff' : '#F9FAFF' }}>
                      <td style={{ ...TD, textAlign: 'left', fontWeight: 700 }}>
                        {q.quarteirao}
                        {q.duplicata && <Chip label="duplicado" size="small" sx={{ ml: 1, fontSize: '0.62rem', bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700 }} />}
                      </td>
                      <td style={{ ...TD, textAlign: 'left', fontSize: '0.72rem', color: '#6B7280' }}>{q.arquivo_origem}</td>
                      <td style={TD}>{q.total}</td>
                      <td style={TD}>{q.total_r || '—'}</td>
                      <td style={TD}>{q.total_c || '—'}</td>
                      <td style={TD}>{q.total_tb || '—'}</td>
                      <td style={TD}>{q.total_pe || '—'}</td>
                      <td style={TD}>{q.total_o || '—'}</td>
                      <td style={TD}><StatusChip s={q.status} /></td>
                      <td style={{ ...TD, textAlign: 'left', fontSize: '0.72rem', color: '#6B7280', maxWidth: 220 }}>{q.obs_status || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </SCard>

          <Card sx={{ border: '1px solid #BFDBFE', borderRadius: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
                <input type="checkbox" checked={revisado} onChange={e => setRevisado(e.target.checked)} style={{ width: 18, height: 18 }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.88rem' }}>Revisei os dados acima e confirmo a importação</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button variant="outlined" onClick={() => { setStep(1); setPreview(null); setRevisado(false); }}>Cancelar</Button>
                <Button variant="contained" disabled={!podeImportar || loading}
                  sx={{ bgcolor: '#065F46', '&:hover': { bgcolor: '#047857' }, fontWeight: 700 }}
                  onClick={confirmarImportacao}>
                  {loading ? <CircularProgress size={18} color="inherit" /> : `Importar ${preview.resumo.total_quarteiroes} quarteirões`}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {step === 3 && resultado && (
        <Alert severity="success" icon={<CheckCircle />}>
          <Typography sx={{ fontWeight: 700 }}>{resultado.resumo.total_importados} quarteirão(ões) importado(s) com sucesso</Typography>
          {resultado.resumo.total_pulados > 0 && <Typography sx={{ fontSize: '0.8rem' }}>{resultado.resumo.total_pulados} pulado(s) por duplicidade.</Typography>}
          {resultado.resumo.total_erros > 0 && <Typography sx={{ fontSize: '0.8rem', color: 'error.main' }}>{resultado.resumo.total_erros} com erro.</Typography>}
          <Button size="small" sx={{ mt: 1 }} onClick={onBack}>Voltar às áreas</Button>
        </Alert>
      )}
    </Box>
  );
}

// ─── TELA 3: Cadastro de Quarteirões ──────────────────────────────────────────

function TelaCadastro({ area, onBack }: { area: Area; onBack: () => void }) {
  const { token } = useAuth();
  const [todosQrts, setQrts] = useState<Quarteirao[]>([]);
  // 20/10/2026 (Walber): quarteirão FORA DE USO fica numa faixa própria, fora do selo e das somas
  const qrts = todosQrts.filter(q => q.status !== 'FORA_DE_USO');
  const foraDeUso = todosQrts.filter(q => q.status === 'FORA_DE_USO');
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState<Quarteirao | null>(null);
  const [imoveis, setImoveis] = useState<Imovel[]>([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    fetch(`${API}/areas/${area.id}/quarteiroes`).then(r => r.json()).then(setQrts).finally(() => setLoading(false));
  }, [area.id]);

  useEffect(() => { carregar(); }, [carregar]);

  async function abrirEditor(q: Quarteirao) {
    setEditando(q); setLoadingImoveis(true);
    const d = await fetch(`${API}/quarteiroes/${q.id}/imoveis`).then(r => r.json());
    setImoveis(d); setLoadingImoveis(false);
  }

  async function adicionarImovel() {
    if (!editando) return;
    await fetch(`${API}/quarteiroes/${editando.id}/imoveis`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tipo: 'R', necessita_revisao: false }),
    });
    const d = await fetch(`${API}/quarteiroes/${editando.id}/imoveis`).then(r => r.json());
    setImoveis(d); carregar();
  }

  async function removerImovel(id: number) {
    await fetch(`${API}/imoveis/${id}`, { method: 'DELETE' });
    setImoveis(prev => prev.filter(i => i.id !== id)); carregar();
  }

  // ── Excluir quarteirão (Sessão 30 — 25/09/2026) ──
  interface ImpactoExclusao {
    quarteirao_id: number; codigo: string; area_id: number; area_nome: string;
    total_imoveis: number; ciclo_ativo_id: number | null; concluido_no_ciclo_ativo: boolean;
    marcas_ciclo: number; boletins_que_citam: number; boletins_ciclo_ativo: number;
    situacoes_ficha_removida: number; area_fechada: boolean;
    status?: string; motivo?: string | null;
  }
  const [qrtExcluir, setQrtExcluir] = useState<Quarteirao | null>(null);
  const [impactoExclusao, setImpactoExclusao] = useState<ImpactoExclusao | null>(null);
  const [carregandoImpacto, setCarregandoImpacto] = useState(false);
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const [msgExclusao, setMsgExclusao] = useState<string | null>(null);

  function lerDetail(d: any, padrao: string): string {
    const det = d?.detail;
    if (Array.isArray(det)) return det.map((x: any) => x?.msg || String(x)).join('; ');
    return det ? String(det) : padrao;
  }

  async function abrirExcluirQuarteirao(q: Quarteirao) {
    setQrtExcluir(q); setImpactoExclusao(null); setErroExclusao(null); setCarregandoImpacto(true);
    try {
      const r = await fetch(`${API}/quarteiroes/${q.id}/impacto-exclusao`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(lerDetail(d, `Erro ${r.status} ao consultar o quarteirão`));
      setImpactoExclusao(d);
    } catch (e: any) {
      setErroExclusao(e?.message || 'Erro ao consultar o quarteirão');
    } finally {
      setCarregandoImpacto(false);
    }
  }

  function fecharExcluirQuarteirao() {
    if (excluindo) return;
    setQrtExcluir(null); setImpactoExclusao(null); setErroExclusao(null);
  }

  async function excluirQuarteirao() {
    if (!qrtExcluir || excluindo) return;
    setExcluindo(true); setErroExclusao(null);
    try {
      const r = await fetch(`${API}/quarteiroes/${qrtExcluir.id}`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErroExclusao(lerDetail(d, `Erro ${r.status} ao excluir`)); return; }
      setQrtExcluir(null); setImpactoExclusao(null);
      setMsgExclusao(`Quarteirão ${d.codigo} excluído. ${d.imoveis_excluidos} imóveis apagados. Cópia de segurança salva.`);
      carregar();
    } catch (e: any) {
      setErroExclusao(e?.message || 'Erro ao excluir');
    } finally {
      setExcluindo(false);
    }
  }

  // ── Registrar quarteirão FORA DE USO (20/10/2026) ──
  const [foraAberto, setForaAberto] = useState(false);
  const [foraCodigo, setForaCodigo] = useState('');
  const [foraMotivo, setForaMotivo] = useState('');
  const [foraErro, setForaErro] = useState<string | null>(null);
  const [foraSalvando, setForaSalvando] = useState(false);
  const [msgForaDeUso, setMsgForaDeUso] = useState<string | null>(null);
  const motivoOk = foraMotivo.trim().length >= 5 && foraMotivo.trim().length <= 120;

  function abrirForaDeUso() {
    setForaCodigo(''); setForaMotivo(''); setForaErro(null); setForaAberto(true);
  }

  async function registrarForaDeUso() {
    if (foraSalvando || !foraCodigo.trim() || !motivoOk) return;
    setForaSalvando(true); setForaErro(null);
    try {
      const r = await fetch(`${API}/areas/${area.id}/quarteiroes/fora-de-uso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ codigo: foraCodigo.trim(), motivo: foraMotivo.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setForaErro(lerDetail(d, `Erro ${r.status} ao registrar`)); return; }
      setForaAberto(false);
      setMsgForaDeUso(d.aviso || 'Quarteirão registrado como fora de uso.');
      carregar();
    } catch (e: any) {
      setForaErro(e?.message || 'Erro ao registrar');
    } finally {
      setForaSalvando(false);
    }
  }

  async function salvarTipo(im: Imovel, novoTipo: string) {
    await fetch(`${API}/imoveis/${im.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...im, tipo: novoTipo }),
    });
    setImoveis(prev => prev.map(i => i.id === im.id ? { ...i, tipo: novoTipo } : i));
    carregar();
  }

  const contagens = imoveis.reduce((acc, im) => { acc[im.tipo] = (acc[im.tipo] || 0) + 1; return acc; }, {} as Record<string, number>);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {msgExclusao && <Alert severity="success" onClose={() => setMsgExclusao(null)}>{msgExclusao}</Alert>}
      {msgForaDeUso && <Alert severity="success" onClose={() => setMsgForaDeUso(null)}>{msgForaDeUso}</Alert>}
      <SCard titulo={`${area.nome} — Quarteirões Cadastrados`} right={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={abrirForaDeUso}
            sx={{ fontSize: '0.72rem', fontWeight: 700, bgcolor: '#fff' }}>Registrar quarteirão fora de uso</Button>
          <Chip label={`${qrts.length} quarteirões`} size="small" sx={{ bgcolor: '#DBEAFE', color: '#1B3A6B', fontWeight: 700 }} />
        </Box>
      }>
        {!loading && foraDeUso.length > 0 && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5 }}>Fora de uso (não contam):</Typography>
            {foraDeUso.map(q => (
              <Box key={q.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.85rem' }}><b>{q.codigo}</b> — {q.motivo || 'sem motivo'}</Typography>
                <Tooltip title="Excluir registro de fora de uso">
                  <IconButton size="small" color="error" onClick={() => abrirExcluirQuarteirao(q)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
              </Box>
            ))}
          </Alert>
        )}
        {loading ? <CircularProgress size={24} /> : (
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>
                {['Código', 'Total', 'R', 'C', 'TB', 'PE', 'O', 'Status', 'Atualizado', '', 'Ações'].map(h => <th key={h} style={TH}>{h}</th>)}
              </tr></thead>
              <tbody>
                {qrts.map((q, i) => (
                  <tr key={q.id} style={{ background: i % 2 === 0 ? '#fff' : '#F9FAFF' }}>
                    <td style={{ ...TD, fontWeight: 800, textAlign: 'left' }}>{q.codigo}</td>
                    <td style={{ ...TD, fontWeight: 700 }}>{q.total_imoveis}</td>
                    <td style={TD}>{q.total_r || '—'}</td>
                    <td style={TD}>{q.total_c || '—'}</td>
                    <td style={TD}>{q.total_tb || '—'}</td>
                    <td style={TD}>{q.total_pe || '—'}</td>
                    <td style={TD}>{q.total_o || '—'}</td>
                    <td style={TD}><StatusChip s={q.status} /></td>
                    <td style={{ ...TD, fontSize: '0.72rem', color: '#6B7280' }}>{new Date(q.atualizado_em).toLocaleDateString('pt-BR')}</td>
                    <td style={TD}>
                      <Button size="small" variant="outlined" startIcon={<Edit sx={{ fontSize: 13 }} />}
                        sx={{ fontSize: '0.7rem', fontWeight: 700, minWidth: 0, px: 1.5 }}
                        onClick={() => abrirEditor(q)}>Editar</Button>
                    </td>
                    <td style={TD}>
                      <Tooltip title="Excluir quarteirão">
                        <IconButton size="small" color="error" onClick={() => abrirExcluirQuarteirao(q)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </SCard>

      {/* Modal de exclusão de quarteirão */}
      <Dialog open={!!qrtExcluir} onClose={fecharExcluirQuarteirao} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1B3A6B', color: '#fff', fontWeight: 700 }}>
          Excluir quarteirão {impactoExclusao?.codigo ?? qrtExcluir?.codigo} — {impactoExclusao?.area_nome ?? area.nome}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
            {carregandoImpacto && <Typography>Carregando…</Typography>}
            {impactoExclusao && <>
              <Typography sx={{ fontSize: '0.95rem' }}>Imóveis que serão apagados: <b>{impactoExclusao.total_imoveis}</b></Typography>
              <Typography sx={{ fontSize: '0.95rem' }}>Concluído no ciclo atual: <b>{impactoExclusao.concluido_no_ciclo_ativo ? 'Sim' : 'Não'}</b></Typography>
              <Typography sx={{ fontSize: '0.95rem' }}>Marcas de ciclo que serão apagadas: <b>{impactoExclusao.marcas_ciclo}</b></Typography>
              <Typography sx={{ fontSize: '0.95rem' }}>Boletins FAD-01 que citam este quarteirão: <b>{impactoExclusao.boletins_que_citam}</b> (no ciclo atual: {impactoExclusao.boletins_ciclo_ativo})</Typography>
              {impactoExclusao.situacoes_ficha_removida > 0 && (
                <Typography sx={{ fontSize: '0.95rem' }}>Registros antigos de situação de imóvel que serão apagados: <b>{impactoExclusao.situacoes_ficha_removida}</b></Typography>
              )}
              {impactoExclusao.status === 'FORA_DE_USO' && (
                <Alert severity="info">Registro de quarteirão fora de uso ({impactoExclusao.motivo || 'sem motivo'}). Excluir não muda nenhum número, por isso é permitido mesmo com a área fechada.</Alert>
              )}
              {impactoExclusao.area_fechada && impactoExclusao.status !== 'FORA_DE_USO' && (
                <Alert severity="error">A área está FECHADA no ciclo atual. Reabra o fechamento da área (módulo Fechamento de Área) antes de excluir quarteirões.</Alert>
              )}
              {!impactoExclusao.area_fechada && impactoExclusao.boletins_que_citam > 0 && (
                <Alert severity="warning">Os boletins FAD-01 NÃO serão alterados. Depois da exclusão, esse número de quarteirão passará a aparecer como 'não cadastrado' no painel e no fechamento da área.</Alert>
              )}
              <Alert severity="info">Uma cópia de segurança do quarteirão e dos imóveis será salva no servidor antes de apagar.</Alert>
            </>}
            {erroExclusao && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{erroExclusao}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={fecharExcluirQuarteirao} disabled={excluindo} sx={{ fontWeight: 700 }}>Cancelar</Button>
          <Button variant="contained" color="error" sx={{ fontWeight: 700 }}
            disabled={!impactoExclusao || (impactoExclusao.area_fechada && impactoExclusao.status !== 'FORA_DE_USO') || excluindo || carregandoImpacto}
            onClick={excluirQuarteirao}>{excluindo ? 'Excluindo…' : 'Excluir quarteirão'}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal: registrar quarteirão fora de uso (20/10/2026) */}
      <Dialog open={foraAberto} onClose={() => !foraSalvando && setForaAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1B3A6B', color: '#fff', fontWeight: 700 }}>
          Registrar quarteirão fora de uso — {area.nome}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Typography sx={{ fontSize: '0.9rem', color: '#374151' }}>
              Fica fora de todos os totais e conclusões; aparece no Resumo do RG (FA-D-06) no lugar de DESAPARECIDO.
            </Typography>
            <TextField label="Código do quarteirão" value={foraCodigo} autoComplete="off"
              onChange={e => setForaCodigo(e.target.value)} placeholder="16" size="small" sx={{ maxWidth: 200 }} />
            <TextField label="Motivo" value={foraMotivo} autoComplete="off"
              onChange={e => setForaMotivo(e.target.value)} placeholder="QUARTEIRÃO ENGLOBADO NO Q15"
              slotProps={{ htmlInput: { maxLength: 120 } }} size="small" fullWidth
              helperText={`${foraMotivo.trim().length}/120 — mínimo 5 caracteres. Ex.: QUARTEIRÃO ENGLOBADO NO Q15`}
              error={foraMotivo.length > 0 && !motivoOk} />
            {foraErro && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{foraErro}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setForaAberto(false)} disabled={foraSalvando} sx={{ fontWeight: 700 }}>Cancelar</Button>
          <Button variant="contained" sx={{ fontWeight: 700 }}
            disabled={foraSalvando || !foraCodigo.trim() || !motivoOk}
            onClick={registrarForaDeUso}>{foraSalvando ? 'Registrando…' : 'Registrar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de edição de imóveis */}
      <Dialog open={!!editando} onClose={() => setEditando(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1B3A6B', color: '#fff', fontWeight: 700 }}>
          Quarteirão {editando?.codigo} — Imóveis
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {loadingImoveis ? <CircularProgress /> : <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {['R', 'C', 'TB', 'PE', 'O'].map(t => (
                <Chip key={t} label={`${t}: ${contagens[t] || 0}`} size="small"
                  sx={{ bgcolor: '#DBEAFE', color: '#1B3A6B', fontWeight: 700 }} />
              ))}
              <Chip label={`Total: ${imoveis.length}`} size="small" sx={{ bgcolor: '#F3F4F6', color: '#374151', fontWeight: 700 }} />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  {['Logradouro', 'Lado', 'Nº', 'Seq.', 'Tipo', ''].map(h => <th key={h} style={{ ...TH, textAlign: h === 'Logradouro' ? 'left' : 'center' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {imoveis.map((im, i) => (
                    <tr key={im.id} style={{ background: im.necessita_revisao ? '#FFFBEB' : i % 2 === 0 ? '#fff' : '#F9FAFF' }}>
                      <td style={{ ...TD, textAlign: 'left' }}>{im.logradouro || '—'}</td>
                      <td style={TD}>{im.lado || '—'}</td>
                      <td style={TD}>{im.numero || '—'}</td>
                      <td style={TD}>{im.sequencia || '—'}</td>
                      <td style={TD}>
                        <select value={im.tipo} onChange={e => salvarTipo(im, e.target.value)}
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontFamily: 'inherit' }}>
                          {['R', 'C', 'TB', 'PE', 'O'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={TD}>
                        <IconButton size="small" color="error" onClick={() => removerImovel(im.id)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button startIcon={<Add />} variant="outlined" sx={{ fontWeight: 700 }} onClick={adicionarImovel}>Adicionar imóvel</Button>
          <Button variant="contained" sx={{ bgcolor: '#065F46', '&:hover': { bgcolor: '#047857' }, fontWeight: 700 }} onClick={() => setEditando(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── TELA 4: Painel de Controle ────────────────────────────────────────────────

function TelaPainel({ area, onBack, onVerDetalhe }: { area: Area; onBack: () => void; onVerDetalhe: (q: PainelQrt, lista: PainelQrt[], cicloId: number) => void }) {
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [cicloId, setCicloId] = useState<number | null>(null);
  const [painel, setPainel] = useState<PainelData | null>(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState<number | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string | null>(null);

  useEffect(() => {
    fetch(`/api/ciclos/`).then(r => r.json()).then((d: any) => {
      const lista: Ciclo[] = Array.isArray(d) ? d : d.ciclos || d.data || [];
      setCiclos(lista);
      if (lista.length) {
        const ativo = lista.find(c => c.ativo);
        setCicloId((ativo ?? lista[lista.length - 1]).id);
      }
    });
  }, []);

  const carregarPainel = useCallback(() => {
    if (!cicloId) return;
    setLoading(true);
    fetch(`${API}/areas/${area.id}/painel?ciclo_id=${cicloId}`)
      .then(r => r.json()).then(setPainel).finally(() => setLoading(false));
  }, [area.id, cicloId]);

  useEffect(() => { carregarPainel(); }, [carregarPainel]);

  async function toggleConclusao(q: PainelQrt) {
    setToggling(q.id);
    await fetch(`${API}/quarteiroes/${q.id}/conclusao`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ciclo_id: cicloId, concluido: !q.concluido, concluido_por: 'supervisor' }),
    });
    carregarPainel();
    setToggling(null);
  }

  const corCard = (q: PainelQrt) => {
    if (q.concluido) return { bg: '#D1FAE5', border: '#A7F3D0', text: '#065F46' };
    if (q.trabalhado_neste_ciclo) return { bg: '#FEF3C7', border: '#FDE68A', text: '#92400E' };
    return { bg: '#F3F4F6', border: '#D1D5DB', text: '#6B7280' };
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Seletor + métricas */}
      <SCard titulo={`${area.nome} — Painel de Controle`} right={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <select value={cicloId || ''} onChange={e => setCicloId(Number(e.target.value))}
            style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #D1D5DB', fontFamily: 'inherit', fontWeight: 600, background: '#fff' }}>
            {ciclos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <IconButton size="small" onClick={carregarPainel} sx={{ color: '#BFDBFE' }}><Refresh /></IconButton>
        </Box>
      }>
        {painel && (
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 260px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Quarteirões concluídos</Typography>
                <Typography sx={{ fontWeight: 800, color: '#0F2347' }}>{painel.metricas.concluidos}/{painel.metricas.total_quarteiroes} ({painel.metricas.pct_quarteiroes}%)</Typography>
              </Box>
              <LinearProgress variant="determinate" value={painel.metricas.pct_quarteiroes}
                sx={{ height: 12, borderRadius: 99, bgcolor: '#E5E7EB', '& .MuiLinearProgress-bar': { bgcolor: '#065F46', borderRadius: 99 } }} />
            </Box>
            <Box sx={{ flex: '1 1 260px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Imóveis cobertos</Typography>
                <Typography sx={{ fontWeight: 800, color: '#0F2347' }}>{painel.metricas.imoveis_cobertos}/{painel.metricas.total_imoveis} ({painel.metricas.pct_imoveis}%)</Typography>
              </Box>
              <LinearProgress variant="determinate" value={painel.metricas.pct_imoveis}
                sx={{ height: 12, borderRadius: 99, bgcolor: '#E5E7EB', '& .MuiLinearProgress-bar': { bgcolor: '#1B3A6B', borderRadius: 99 } }} />
            </Box>
          </Box>
        )}
      </SCard>

      {loading && <LinearProgress />}

      {painel && (painel.fora_de_uso || []).length > 0 && (
        <Alert severity="info">
          Quarteirões fora de uso (fora da contagem): {(painel.fora_de_uso || []).map(f => `${f.codigo} — ${f.motivo || 'sem motivo'}`).join(' · ')}
        </Alert>
      )}

      {painel && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2.5, alignItems: 'flex-start' }}>
          {/* Grid de quarteirões */}
          <SCard titulo="Mapa de Quarteirões">
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              {[['#D1FAE5', '#065F46', 'Concluído', 'done'], ['#FEF3C7', '#92400E', 'Pendente', 'worked'], ['#F3F4F6', '#6B7280', 'A trabalhar', 'pending']].map(([bg, color, label, status]) => (
                <Box key={label} onClick={() => setFiltroStatus(filtroStatus === status ? null : status)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.7, cursor: 'pointer',
                    px: 0.7, py: 0.3, borderRadius: 1.5,
                    border: filtroStatus === status ? `1.5px solid ${color}` : '1.5px solid transparent',
                    bgcolor: filtroStatus === status ? `${color}14` : 'transparent',
                  }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: 1, bgcolor: bg, border: `1px solid ${color}` }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151' }}>{label}</Typography>
                </Box>
              ))}
              <Box onClick={() => setFiltroStatus(filtroStatus === 'solo_tb' ? null : 'solo_tb')}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.7, cursor: 'pointer',
                  px: 0.7, py: 0.3, borderRadius: 1.5,
                  border: filtroStatus === 'solo_tb' ? '1.5px solid #92400E' : '1.5px solid transparent',
                  bgcolor: filtroStatus === 'solo_tb' ? '#92400E14' : 'transparent',
                }}>
                <Box sx={{ width: 14, height: 14, borderRadius: 1, border: '3px solid #92400E' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151' }}>1 TB (único)</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 1 }}>
              {painel.quarteiroes.filter(q => {
                if (!filtroStatus) return true;
                if (filtroStatus === 'solo_tb') return q.total_imoveis === 1 && q.total_tb === 1;
                return q.status_painel === filtroStatus;
              }).map(q => {
                const c = corCard(q);
                const soloTB = q.total_imoveis === 1 && q.total_tb === 1;
                return (
                  <Box key={q.id} sx={{
                    bgcolor: c.bg,
                    border: soloTB ? '3px solid #92400E' : `2px solid ${c.border}`,
                    borderRadius: 2, p: 1, textAlign: 'center', transition: 'all .15s',
                    boxShadow: soloTB ? '0 0 0 1px #92400E44' : 'none',
                  }}>
                    <Typography
                      onClick={() => cicloId && onVerDetalhe(q, painel!.quarteiroes, cicloId)}
                      sx={{ fontWeight: 800, fontSize: '0.88rem', color: c.text, fontVariantNumeric: 'tabular-nums',
                        cursor: 'pointer', textDecoration: 'underline', '&:hover': { opacity: 0.75 } }}>
                      {q.codigo}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: c.text, opacity: 0.85 }}>{q.total_imoveis} im.</Typography>
                      {q.total_imoveis > 0 && (() => {
                        // Conferencia dos imoveis (Fase 2) — independente do toggle de concluido
                        const tudoConf = q.sit_conferidos === q.total_imoveis;
                        const temTrabalho = q.concluido || q.trabalhado_neste_ciclo;
                        if (tudoConf)
                          return (
                            <Tooltip title={`Todos os ${q.total_imoveis} imóveis conferidos`}>
                              <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#D1FAE5', border: '1px solid #065F46', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontWeight: 900, color: '#065F46', lineHeight: 1 }}>✓</Box>
                            </Tooltip>
                          );
                        if (temTrabalho)
                          return (
                            <Tooltip title={`${q.sit_conferidos}/${q.total_imoveis} imóveis conferidos — falta conferir`}>
                              <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#FEF3C7', border: '1px solid #F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 900, color: '#92400E', lineHeight: 1 }}>!</Box>
                            </Tooltip>
                          );
                        return null;
                      })()}
                    </Box>
                    {soloTB && <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#92400E', letterSpacing: '0.5px', mt: 0.2 }}>TB</Typography>}
                    <Tooltip title={q.concluido ? 'Desmarcar como concluído' : 'Marcar como concluído'}>
                      <Box sx={{ mt: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, cursor: 'pointer' }}
                        onClick={() => toggleConclusao(q)}>
                        {toggling === q.id
                          ? <CircularProgress size={14} />
                          : <Switch size="small" checked={q.concluido}
                              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#065F46' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#065F46' } }} />
                        }
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </SCard>

          {/* Painel de verificação */}
          <SCard titulo="Verificação" cor="#C2410C">
            {painel.inconsistencias.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle sx={{ fontSize: 36, color: '#065F46', mb: 1 }} />
                <Typography sx={{ fontWeight: 600, color: '#065F46' }}>Nenhuma inconsistência</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {painel.inconsistencias.map((inc, i) => (
                  <Box key={i} sx={{ bgcolor: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 2, p: 1.5, display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
                    <Warning sx={{ fontSize: 18, color: '#991B1B', flexShrink: 0, mt: 0.2 }} />
                    <Typography sx={{ fontSize: '0.78rem', color: '#7F1D1D' }}>{inc.texto}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </SCard>
        </Box>
      )}
    </Box>
  );
}

// ─── TELA 5: Detalhe do Quarteirão ────────────────────────────────────────────

const TIPO_LABEL: Record<string, string> = { R: 'Residência', C: 'Comércio', TB: 'T. Baldio', PE: 'P. Estratégico', O: 'Outros' };
const SITUACAO_COLOR: Record<string, { bg: string; color: string; label: string }> = {
  nao_trabalhado: { bg: '#F3F4F6', color: '#6B7280', label: '—'          },
  trabalhado:     { bg: '#D1FAE5', color: '#065F46', label: 'Trabalhado' },
  fechado:        { bg: '#FEE2E2', color: '#991B1B', label: 'Fechado'    },
  recusado:       { bg: '#FEF3C7', color: '#92400E', label: 'Recusado'   },
  recuperado:     { bg: '#DBEAFE', color: '#1E3A8A', label: 'Recuperado' },
};

// Ordem do clique ciclico no chip de situacao
const CICLO_SITUACAO: Situacao[] = ['nao_trabalhado', 'trabalhado', 'fechado', 'recusado', 'recuperado'];

const TIPO_COLOR: Record<string, { bg: string; color: string }> = {
  R:  { bg: '#DBEAFE', color: '#1B3A6B' },
  C:  { bg: '#D1FAE5', color: '#065F46' },
  TB: { bg: '#FEF3C7', color: '#92400E' },
  PE: { bg: '#FEE2E2', color: '#991B1B' },
  O:  { bg: '#F3F4F6', color: '#374151' },
};

function TelaDetalheQuarteirao({ area, quarteirao, lista, cicloId, onBack, onNavegar }:
  { area: Area; quarteirao: PainelQrt; lista: PainelQrt[]; cicloId: number; onBack: () => void; onNavegar: (q: PainelQrt) => void }) {

  const [imoveis, setImoveis] = useState<ImovelSituacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState<string>('TODOS');
  const [filtroRevisao, setFiltroRevisao] = useState(false);

  const idx = lista.findIndex(q => q.id === quarteirao.id);
  const anterior = idx > 0 ? lista[idx - 1] : null;
  const proximo  = idx < lista.length - 1 ? lista[idx + 1] : null;

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    fetch(`${API}/quarteiroes/${quarteirao.id}/situacao?ciclo_id=${cicloId}`).then(r => r.json())
      .then(d => { if (vivo) { setImoveis(Array.isArray(d) ? d : []); setLoading(false); } });
    return () => { vivo = false; };
  }, [quarteirao, cicloId]);

  const imoveisFiltrados = imoveis.filter(im => {
    if (filtroTipo !== 'TODOS' && im.tipo !== filtroTipo) return false;
    if (filtroRevisao && !im.necessita_revisao) return false;
    return true;
  });
  const porLogradouro = imoveisFiltrados.reduce((acc, im) => {
    const rua = im.logradouro || '(sem logradouro)';
    (acc[rua] = acc[rua] || []).push(im);
    return acc;
  }, {} as Record<string, ImovelSituacao[]>);
  const contagem = imoveis.reduce((acc, im) => { acc[im.tipo] = (acc[im.tipo] || 0) + 1; return acc; }, {} as Record<string, number>);
  const comRevisao = imoveis.filter(im => im.necessita_revisao);

  const corStatus = quarteirao.concluido
    ? { bg: '#D1FAE5', border: '#A7F3D0', text: '#065F46', label: '✓ Concluído' }
    : quarteirao.trabalhado_neste_ciclo
    ? { bg: '#FEF3C7', border: '#FDE68A', text: '#92400E', label: '⏳ Pendente' }
    : { bg: '#F3F4F6', border: '#D1D5DB', text: '#6B7280', label: '○ A trabalhar' };

  const TH2: React.CSSProperties = { background: '#0a1830', color: '#DCE6FB', padding: '8px 12px', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', whiteSpace: 'nowrap' };
  const TD2: React.CSSProperties = { padding: '7px 12px', fontSize: '0.82rem', borderBottom: '1px solid #E5E7EB', fontVariantNumeric: 'tabular-nums' };

  function imprimir() {
    const linhas = Object.entries(porLogradouro).map(([rua, ims]) =>
      `<div class="rua"><div class="rua-titulo">${rua} <span class="rua-count">${ims.length} imóvel(is)</span></div>
       <table><thead><tr><th>Lado</th><th>Nº</th><th>Seq.</th><th>Comp.</th><th>Tipo</th><th>Observação</th></tr></thead>
       <tbody>${ims.map((im, i) => `<tr class="${im.necessita_revisao ? 'revisao' : i % 2 === 0 ? 'par' : 'impar'}">
         <td>${im.lado || '—'}</td><td><b>${im.numero || '—'}</b></td><td>${im.sequencia || '—'}</td><td>${im.complemento || '—'}</td>
         <td><span class="tipo tipo-${im.tipo}">${im.tipo}</span></td><td class="obs">${im.observacao || '—'}</td></tr>`).join('')}</tbody></table></div>`).join('');
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>Quarteirão ${quarteirao.codigo} — ${area.nome}</title>
      <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:11px;color:#000;padding:16px}h1{font-size:16px;margin-bottom:2px}
      .sub{font-size:11px;color:#555;margin-bottom:12px}.chips{display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap}.chip{padding:3px 10px;border-radius:12px;font-size:11px;font-weight:700;border:1px solid #ccc}
      .chip-R,.tipo-R{background:#DBEAFE;color:#1B3A6B}.chip-C,.tipo-C{background:#D1FAE5;color:#065F46}.chip-TB,.tipo-TB{background:#FEF3C7;color:#92400E}.chip-PE,.tipo-PE{background:#FEE2E2;color:#991B1B}.chip-O,.tipo-O{background:#F3F4F6;color:#374151}
      .alerta{background:#FFFBEB;border:1px solid #FDE68A;padding:8px 12px;border-radius:6px;margin-bottom:12px;font-size:10px}.rua{margin-bottom:14px;page-break-inside:avoid}
      .rua-titulo{background:#1B3A6B;color:#fff;padding:6px 10px;font-weight:700;font-size:12px;border-radius:4px 4px 0 0}.rua-count{font-size:10px;font-weight:400;opacity:.8;margin-left:8px}
      table{width:100%;border-collapse:collapse}th{background:#0a1830;color:#DCE6FB;padding:5px 8px;font-size:10px;text-align:left}tr.par td{background:#fff}tr.impar td{background:#F9FAFF}tr.revisao td{background:#FFFBEB}
      td{padding:4px 8px;border-bottom:1px solid #E5E7EB}.tipo{padding:2px 7px;border-radius:10px;font-weight:700;font-size:10px}.obs{font-size:9px;color:#6B7280;max-width:180px}@media print{body{padding:8px}}</style></head>
      <body><h1>Quarteirão ${quarteirao.codigo}</h1><div class="sub">${area.nome} · ${quarteirao.total_imoveis} imóveis · ${new Date().toLocaleDateString('pt-BR')}</div>
      <div class="chips">${['R','C','TB','PE','O'].map(t => contagem[t] ? `<span class="chip chip-${t}">${t}: ${contagem[t]}</span>` : '').join('')}</div>
      ${comRevisao.length > 0 ? `<div class="alerta">⚠ ${comRevisao.length} imóvel(is) com observação para revisão</div>` : ''}${linhas}</body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F0F4FF', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #0F2347, #1B3A6B)' }} className="no-print">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton color="inherit" onClick={onBack}><ArrowBack /></IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>Quarteirão {quarteirao.codigo}</Typography>
            <Typography sx={{ fontSize: '0.72rem', opacity: 0.8 }}>{area.nome}</Typography>
          </Box>
          <Tooltip title={anterior ? `← Quarteirão ${anterior.codigo}` : 'Primeiro quarteirão'}><span>
            <IconButton color="inherit" disabled={!anterior} onClick={() => anterior && onNavegar(anterior)}><ArrowBack sx={{ fontSize: 18 }} /></IconButton>
          </span></Tooltip>
          <Tooltip title={proximo ? `Quarteirão ${proximo.codigo} →` : 'Último quarteirão'}><span>
            <IconButton color="inherit" disabled={!proximo} onClick={() => proximo && onNavegar(proximo)}><ArrowBack sx={{ fontSize: 18, transform: 'rotate(180deg)' }} /></IconButton>
          </span></Tooltip>
          <Button variant="outlined" startIcon={<Print />} onClick={imprimir}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', fontWeight: 700, ml: 1 }}>
            Imprimir
          </Button>
        </Toolbar>
      </AppBar>

      <Box className="print-detalhe" sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Card sx={{ borderRadius: 3, border: `2px solid ${corStatus.border}`, overflow: 'hidden' }}>
          <Box sx={{ background: 'linear-gradient(135deg, #0F2347, #1B3A6B)', px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#fff', fontVariantNumeric: 'tabular-nums' }}>Quarteirão {quarteirao.codigo}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>{area.nome} · {quarteirao.total_imoveis} imóveis</Typography>
            </Box>
            <Chip label={corStatus.label} size="small" sx={{ bgcolor: corStatus.bg, color: corStatus.text, fontWeight: 800, fontSize: '0.78rem', px: 1 }} />
          </Box>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {['R','C','TB','PE','O'].map(t => (
                <Box key={t} sx={{ textAlign: 'center', minWidth: 60, p: 1, borderRadius: 2, bgcolor: TIPO_COLOR[t].bg, border: `1px solid ${TIPO_COLOR[t].bg}` }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: TIPO_COLOR[t].color, fontVariantNumeric: 'tabular-nums' }}>{contagem[t] || 0}</Typography>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: TIPO_COLOR[t].color, textTransform: 'uppercase' }}>{t}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {comRevisao.length > 0 && (
          <Alert severity="warning" icon={<Warning />}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{comRevisao.length} imóvel(is) com observação para revisão</Typography>
            {comRevisao.map(im => (
              <Typography key={im.id} sx={{ fontSize: '0.78rem', mt: 0.3 }}>• {im.logradouro || '?'} nº {im.numero || '—'} ({im.tipo}) — {im.observacao}</Typography>
            ))}
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, border: '1px solid #BFDBFE', overflow: 'hidden' }} className="no-print">
          <Box sx={{ background: '#1B3A6B', px: 2.5, py: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#fff' }}>Filtros</Typography>
          </Box>
          <CardContent sx={{ p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {['TODOS','R','C','TB','PE','O'].map(t => (
              <Button key={t} size="small" variant={filtroTipo === t ? 'contained' : 'outlined'} onClick={() => setFiltroTipo(t)}
                sx={{ fontWeight: 700, minWidth: 44, fontSize: '0.72rem', ...(filtroTipo === t ? { bgcolor: '#1B3A6B', '&:hover': { bgcolor: '#0F2347' } } : {}) }}>
                {t === 'TODOS' ? 'Todos' : t}
              </Button>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Button size="small" variant={filtroRevisao ? 'contained' : 'outlined'} color="warning" onClick={() => setFiltroRevisao(!filtroRevisao)}
              startIcon={<Warning sx={{ fontSize: 14 }} />} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
              Só revisão {comRevisao.length > 0 && `(${comRevisao.length})`}
            </Button>
            <Typography sx={{ ml: 'auto', fontSize: '0.72rem', color: '#6B7280', fontWeight: 600 }}>{imoveisFiltrados.length} de {imoveis.length} exibidos</Typography>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          Object.entries(porLogradouro).map(([rua, ims]) => (
            <Card key={rua} className="rua-block" sx={{ borderRadius: 3, border: '1px solid #BFDBFE', overflow: 'hidden' }}>
              <Box className="rua-header" sx={{ background: '#1B3A6B', px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff', flex: 1 }}>{rua}</Typography>
                <Chip label={`${ims.length} imóvel(is)`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: '0.68rem' }} />
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead><tr>{['Lado','Nº','Seq.','Comp.','Tipo','Obs.'].map(h => (
                    <th key={h} style={{ ...TH2, textAlign: h === 'Tipo' ? 'center' : 'left' }}>{h}</th>))}</tr></thead>
                  <tbody>
                    {ims.map((im, i) => (
                      <tr key={im.id} style={{ background: im.necessita_revisao ? '#FFFBEB' : i % 2 === 0 ? '#fff' : '#F9FAFF' }}>
                        <td style={TD2}>{im.lado || '—'}</td>
                        <td style={{ ...TD2, fontWeight: 700 }}>{im.numero || '—'}</td>
                        <td style={TD2}>{im.sequencia || '—'}</td>
                        <td style={TD2}>{im.complemento || '—'}</td>
                        <td style={{ ...TD2, textAlign: 'center' }}>
                          <Chip label={im.tipo} size="small" sx={{ bgcolor: TIPO_COLOR[im.tipo]?.bg || '#F3F4F6', color: TIPO_COLOR[im.tipo]?.color || '#374151', fontWeight: 700, fontSize: '0.68rem', minWidth: 36 }} />
                        </td>
                        <td style={{ ...TD2, fontSize: '0.72rem', color: im.necessita_revisao ? '#92400E' : '#9CA3AF', maxWidth: 200 }}>{im.observacao || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Card>
          ))
        )}
        {imoveisFiltrados.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 6, color: '#6B7280' }}><Typography sx={{ fontWeight: 600 }}>Nenhum imóvel encontrado para este filtro</Typography></Box>
        )}
      </Box>
    </Box>
  );
}


// ─── APP PRINCIPAL ─────────────────────────────────────────────────────────────

type Tela = 'lista' | 'importar' | 'cadastro' | 'painel' | 'detalhe';

export default function CadastroArea({ onVoltar }: { onVoltar: () => void }) {
  const [tela, setTela] = useState<Tela>('lista');
  const [areaSel, setAreaSel] = useState<Area | null>(null);
  const [qrtSel, setQrtSel] = useState<PainelQrt | null>(null);
  const [qrtLista, setQrtLista] = useState<PainelQrt[]>([]);
  const [cicloSel, setCicloSel] = useState<number | null>(null);

  function selArea(area: Area, destino: 'importar' | 'cadastro' | 'painel') {
    setAreaSel(area); setTela(destino);
  }
  function voltar() { setTela('lista'); setAreaSel(null); }

  const titulo: Record<Tela, string> = {
    lista: 'Cadastro e Controle de Área',
    importar: `Importar RG — ${areaSel?.nome ?? ''}`,
    cadastro: `Cadastro — ${areaSel?.nome ?? ''}`,
    painel: `Painel — ${areaSel?.nome ?? ''}`,
    detalhe: `Quarteirão ${qrtSel?.codigo ?? ''} — ${areaSel?.nome ?? ''}`,
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F0F4FF' }}>
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #0F2347, #1B3A6B)' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={tela === 'lista' ? onVoltar : voltar} sx={{ mr: 1 }}><ArrowBack /></IconButton>
          <MapIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>{titulo[tela]}</Typography>
        </Toolbar>
      </AppBar>

      {tela === 'lista'    && <TelaListaAreas onSelect={selArea} />}
      {tela === 'importar' && areaSel && <TelaImportacao area={areaSel} onBack={voltar} />}
      {tela === 'cadastro' && areaSel && <TelaCadastro area={areaSel} onBack={voltar} />}
      {tela === 'painel'   && areaSel && <TelaPainel area={areaSel} onBack={voltar}
        onVerDetalhe={(q, lista, cicloId) => { setQrtSel(q); setQrtLista(lista); setCicloSel(cicloId); setTela('detalhe'); }} />}
      {tela === 'detalhe'  && areaSel && qrtSel && cicloSel !== null && <TelaDetalheQuarteirao
        area={areaSel} quarteirao={qrtSel} lista={qrtLista} cicloId={cicloSel}
        onBack={() => setTela('painel')}
        onNavegar={(q) => setQrtSel(q)} />}
    </Box>
  );
}
